import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Accept, FileError, FileRejection } from 'react-dropzone';
import { type DynamicTextProp, useDynamicTextResolver } from '../../hooks/useDynamicTextResolver';
import { useLatestRef } from '../../hooks/useLatestRef';
import type { FileProcessingConfig, FileProcessingState } from './types';
import { getErrorMessage, getFilesFromClipboard, isImageFile, validatePastedFiles } from './utils';

/** Manages a single file's preview URL with automatic cleanup. */
export function useFilePreview(file: File | null): string | null {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file || !isImageFile(file)) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return previewUrl;
}

interface UseFileProcessingReturn {
  /** Current processing states for all files */
  processingStates: Map<File, FileProcessingState>;
  /** Whether any file is currently being processed */
  isProcessing: boolean;
  /** Process a batch of files */
  processFiles: (files: File[]) => void;
  /** Cancel processing for specific files or all files */
  cancelProcessing: (files?: File[]) => void;
  /** Clear processing state for specific files */
  clearProcessingState: (files: File[]) => void;
  /** Get aggregate progress (0-100) across all files */
  aggregateProgress: number;
}

const DEFAULT_PROCESSING_CONFIG = {
  concurrency: 3,
  timeout: 30000,
};

/**
 * Hook to manage file processing with concurrency control and progress tracking.
 *
 * @example
 * const { processingStates, processFiles } = useFileProcessing({
 *   process: async (file, { signal, onProgress, setStatusText }) => {
 *     setStatusText('Scanning...');
 *     await moderate(file, signal);
 *     setStatusText('Uploading...');
 *     return await uploadWithProgress(file, { signal, onProgress });
 *   },
 *   concurrency: 2,
 * });
 */
export function useFileProcessing(config?: FileProcessingConfig): UseFileProcessingReturn {
  const process = config?.process;
  const concurrency = config?.concurrency ?? DEFAULT_PROCESSING_CONFIG.concurrency;
  const timeout = config?.timeout ?? DEFAULT_PROCESSING_CONFIG.timeout;

  const [processingStates, setProcessingStates] = useState<Map<File, FileProcessingState>>(new Map());
  const [isProcessing, setIsProcessing] = useState(false);
  const abortControllersRef = useRef<Map<File, AbortController>>(new Map());
  const activeCountRef = useRef(0);
  const queueRef = useRef<File[]>([]);
  const resultsRef = useRef<Map<File, FileProcessingState>>(new Map());
  const batchFilesRef = useRef<File[]>([]);

  const processRef = useLatestRef(process);
  const onStartRef = useLatestRef(config?.onStart);
  const onFileCompleteRef = useLatestRef(config?.onFileComplete);
  const onCompleteRef = useLatestRef(config?.onComplete);
  const concurrencyRef = useLatestRef(concurrency);
  const timeoutRef = useLatestRef(timeout);

  const aggregateProgress = useMemo(() => {
    if (processingStates.size === 0) return 0;

    let totalProgress = 0;
    let fileCount = 0;

    processingStates.forEach((state) => {
      fileCount++;
      switch (state.status) {
        case 'pending':
          break;
        case 'processing':
          totalProgress += state.progress ?? 0;
          break;
        case 'complete':
        case 'error':
          totalProgress += 100;
          break;
      }
    });

    return Math.round(totalProgress / fileCount);
  }, [processingStates]);

  const updateFileState = useCallback(
    (file: File, stateOrUpdater: FileProcessingState | ((prev: FileProcessingState) => FileProcessingState)) => {
      setProcessingStates((prev) => {
        const next = new Map(prev);
        const current = prev.get(file) ?? { status: 'pending' };
        next.set(file, typeof stateOrUpdater === 'function' ? stateOrUpdater(current) : stateOrUpdater);
        return next;
      });
    },
    [],
  );

  // ── Queue processing pipeline ──
  // These three functions are mutually recursive (drainQueue → processSingle → finish → drainQueue).
  // Since they only access refs and the stable `updateFileState`, we define them as plain functions
  // and avoid the useCallback/useLatestRef ceremony entirely.

  function finishFile(file: File, state: FileProcessingState) {
    updateFileState(file, state);
    resultsRef.current.set(file, state);
    abortControllersRef.current.delete(file);
    activeCountRef.current--;
    onFileCompleteRef.current?.(file, state);
    drainQueue();
  }

  async function processSingleFile(file: File) {
    const controller = new AbortController();
    abortControllersRef.current.set(file, controller);

    const createTimeoutPromise = () =>
      new Promise<never>((_, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Processing timeout'));
        }, timeoutRef.current);
        controller.signal.addEventListener('abort', () => clearTimeout(timeoutId));
      });

    try {
      updateFileState(file, { status: 'processing' });

      const onProgress = (percent: number) => {
        if (!controller.signal.aborted) {
          updateFileState(file, (prev) => ({
            ...prev,
            status: 'processing',
            progress: Math.min(100, Math.max(0, percent)),
          }));
        }
      };

      const setStatusText = (text: string) => {
        if (!controller.signal.aborted) {
          updateFileState(file, (prev) => ({
            ...prev,
            status: 'processing',
            statusText: text,
          }));
        }
      };

      const result = await Promise.race([
        processRef.current!(file, { signal: controller.signal, onProgress, setStatusText }),
        createTimeoutPromise(),
      ]);

      if (controller.signal.aborted) {
        finishFile(file, { status: 'error', error: { code: 'aborted', message: 'Processing cancelled' } });
        return;
      }

      finishFile(file, { status: 'complete', progress: 100, result: result ?? undefined });
    } catch (err) {
      if (!controller.signal.aborted) {
        const error: FileError = {
          code: 'processing-error',
          message: err instanceof Error ? err.message : 'Processing failed',
        };
        finishFile(file, { status: 'error', error });
      }
    }
  }

  function drainQueue() {
    while (activeCountRef.current < concurrencyRef.current && queueRef.current.length > 0) {
      const file = queueRef.current.shift();
      if (!file) break;

      activeCountRef.current++;
      processSingleFile(file);
    }

    if (activeCountRef.current === 0 && queueRef.current.length === 0 && batchFilesRef.current.length > 0) {
      setIsProcessing(false);
      onCompleteRef.current?.(new Map(resultsRef.current));
      batchFilesRef.current = [];
      resultsRef.current.clear();
    }
  }

  const processFiles = useCallback((files: File[]) => {
    if (files.length === 0 || !processRef.current) return;

    setProcessingStates((prev) => {
      const next = new Map(prev);
      files.forEach((file) => {
        next.set(file, { status: 'pending' });
      });
      return next;
    });

    batchFilesRef.current = [...files];
    resultsRef.current.clear();
    queueRef.current.push(...files);
    setIsProcessing(true);
    onStartRef.current?.(files);
    drainQueue();
  }, []);

  const cancelProcessing = useCallback((files?: File[]) => {
    const filesToCancel = files ?? Array.from(abortControllersRef.current.keys());

    filesToCancel.forEach((file) => {
      const controller = abortControllersRef.current.get(file);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(file);
      }

      const queueIndex = queueRef.current.indexOf(file);
      if (queueIndex !== -1) {
        queueRef.current.splice(queueIndex, 1);
      }
    });

    if (!files) {
      queueRef.current = [];
      activeCountRef.current = 0;
      setIsProcessing(false);
    }
  }, []);

  const clearProcessingState = useCallback((files: File[]) => {
    setProcessingStates((prev) => {
      const next = new Map(prev);
      files.forEach((file) => next.delete(file));
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current.clear();
    };
  }, []);

  return {
    processingStates,
    isProcessing,
    processFiles,
    cancelProcessing,
    clearProcessingState,
    aggregateProgress,
  };
}

interface UseValidationStateOptions {
  status: 'default' | 'error';
  internalRejections: FileRejection[];
  processingStates: Map<File, FileProcessingState>;
  files: File[];
  previewHandlesError: boolean;
  isDragActive: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
  isEmpty: boolean;
  text?: {
    fileTooLarge?: (maxSize: string) => string;
    fileInvalidType?: string | ((fileName: string) => string);
    tooManyFiles?: string;
    announcements?: {
      dragAccept?: string;
      dragReject?: string;
    };
  };
  maxSize?: number;
}

interface ValidationState {
  effectiveStatus: 'default' | 'error';
  validationMessage: string | null;
  dragAnnouncement: string | null;
}

/** Derives effective validation status, error messages, and drag announcements from current state. */
export function useValidationState({
  status,
  internalRejections,
  processingStates,
  files,
  previewHandlesError,
  isDragActive,
  isDragAccept,
  isDragReject,
  isEmpty,
  text,
  maxSize,
}: UseValidationStateOptions): ValidationState {
  const hasRejections = internalRejections.length > 0;
  const hasProcessingErrors = [...processingStates.values()].some((s) => s.status === 'error');
  const effectiveStatus = status === 'default' && (hasRejections || hasProcessingErrors) ? 'error' : status;

  const autoErrorMessage = useMemo(() => {
    if (hasRejections) {
      const firstRejection = internalRejections[0];
      if (firstRejection) {
        return getErrorMessage(firstRejection, text, { maxSize });
      }
    }

    if (hasProcessingErrors && !previewHandlesError) {
      const errorEntry = [...processingStates.entries()].find(([, s]) => s.status === 'error');
      if (errorEntry) {
        const [file, state] = errorEntry;
        const errorMsg = state.error?.message ?? 'Validation failed';
        return files.length > 1 ? `${file.name}: ${errorMsg}` : errorMsg;
      }
    }

    return null;
  }, [hasRejections, internalRejections, text, maxSize, hasProcessingErrors, previewHandlesError, processingStates, files.length]);

  const dragRejectMessage =
    isDragActive && isDragReject && !isEmpty && !previewHandlesError
      ? ((typeof text?.fileInvalidType === 'string' ? text.fileInvalidType : null) ?? 'File not allowed')
      : null;

  const validationMessage = autoErrorMessage || dragRejectMessage;

  const announcements = text?.announcements;
  const dragAnnouncement = useMemo(() => {
    if (!isDragActive) return null;
    if (isDragReject) return announcements?.dragReject ?? 'File not allowed.';
    if (isDragAccept) return announcements?.dragAccept ?? 'Ready to upload. Release to add file.';
    return null;
  }, [isDragActive, isDragAccept, isDragReject, announcements?.dragReject, announcements?.dragAccept]);

  return { effectiveStatus, validationMessage, dragAnnouncement };
}

interface UsePasteUploadOptions {
  disabled: boolean;
  noPaste: boolean;
  accept?: Accept;
  maxSize?: number;
  maxFiles?: number;
  multiple: boolean;
  filesRef: React.RefObject<File[]>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onPaste: (accepted: File[], rejected: FileRejection[]) => void;
}

/** Handles clipboard paste-to-upload with validation, scoped to a wrapper element. */
export function usePasteUpload({
  disabled,
  noPaste,
  accept,
  maxSize,
  maxFiles,
  multiple,
  filesRef,
  wrapperRef,
  onPaste,
}: UsePasteUploadOptions) {
  const onPasteRef = useLatestRef(onPaste);

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      if (disabled || noPaste) return;

      const pastedFiles = getFilesFromClipboard(event);
      if (pastedFiles.length === 0) return;

      event.preventDefault();

      const { accepted, rejected } = validatePastedFiles(pastedFiles, {
        accept,
        maxSize,
        maxFiles,
        multiple,
        currentFileCount: filesRef.current.length,
      });
      onPasteRef.current(accepted, rejected);
    },
    [disabled, noPaste, accept, maxSize, maxFiles, multiple, filesRef, onPasteRef],
  );

  useEffect(() => {
    if (noPaste) return;

    const handleDocumentPaste = (event: ClipboardEvent) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const activeElement = document.activeElement;
      if (wrapper.contains(activeElement)) {
        handlePaste(event);
      }
    };

    document.addEventListener('paste', handleDocumentPaste);
    return () => document.removeEventListener('paste', handleDocumentPaste);
  }, [handlePaste, noPaste, wrapperRef]);
}

interface AnnouncementResolvers {
  /** Announces processing start. Args: (count, fileName?) - fileName provided when count === 1 */
  processing: (count: number, fileName?: string) => string;
  /** Announces processing success. Args: (count, fileName?) - fileName provided when count === 1 */
  processed: (count: number, fileName?: string) => string;
  /** Announces processing failure. Args: (failedCount, totalCount, message, fileName?) - fileName provided when failedCount === 1 */
  processingFailed: (failedCount: number, totalCount: number, message: string, fileName?: string) => string;
  /** Announces files added (no processing). Args: (count, fileName?) - fileName provided when count === 1 */
  added: (count: number, fileName?: string) => string;
  /** Announces sync rejection on drop. Args: (count, message, fileName?) - fileName provided when count === 1 */
  rejected: (count: number, message: string, fileName?: string) => string;
  /** Announces mixed result. Args: (acceptedCount, rejectedCount, message) */
  mixed: (acceptedCount: number, rejectedCount: number, message: string) => string;
}

/** Consolidates all screen reader announcement resolvers into a single hook. */
export function useAnnouncementResolvers(announcements?: {
  processing?: DynamicTextProp<[count: number, fileName: string | undefined]>;
  processed?: DynamicTextProp<[count: number, fileName: string | undefined]>;
  processingFailed?: DynamicTextProp<[failedCount: number, totalCount: number, message: string, fileName: string | undefined]>;
  added?: DynamicTextProp<[count: number, fileName: string | undefined]>;
  rejected?: DynamicTextProp<[count: number, message: string, fileName: string | undefined]>;
  mixed?: DynamicTextProp<[acceptedCount: number, rejectedCount: number, message: string]>;
}): AnnouncementResolvers {
  return {
    processing: useDynamicTextResolver(announcements?.processing, (count: number, fileName?: string) =>
      count === 1 && fileName ? `Processing ${fileName}...` : `Processing ${count} files...`,
    ),
    processed: useDynamicTextResolver(announcements?.processed, (count: number, fileName?: string) =>
      count === 1 && fileName ? `${fileName} processed.` : `${count} files processed.`,
    ),
    processingFailed: useDynamicTextResolver(
      announcements?.processingFailed,
      (failedCount: number, totalCount: number, msg: string, fileName?: string) => {
        if (failedCount === totalCount) {
          return failedCount === 1 && fileName ? `${fileName} failed. ${msg}` : `${failedCount} files failed. ${msg}`;
        }
        const successCount = totalCount - failedCount;
        return `${successCount} ${successCount === 1 ? 'file' : 'files'} processed, ${failedCount} failed. ${msg}`;
      },
    ),
    added: useDynamicTextResolver(announcements?.added, (count: number, fileName?: string) =>
      count === 1 && fileName ? `${fileName} added.` : `${count} files added.`,
    ),
    rejected: useDynamicTextResolver(announcements?.rejected, (count: number, msg: string, fileName?: string) =>
      count === 1 && fileName ? `${fileName} rejected. ${msg}` : `${count} files rejected. ${msg}`,
    ),
    mixed: useDynamicTextResolver(
      announcements?.mixed,
      (acceptedCount: number, rejectedCount: number, msg: string) =>
        `${acceptedCount} ${acceptedCount === 1 ? 'file' : 'files'} added, ${rejectedCount} rejected. ${msg}`,
    ),
  };
}
